/**
 * RoeTile — Paie › Relevés d'emploi : brouillons auto-créés au départ d'un employé, liste des RE (brouillon / émis / modifié), création manuelle. Batch #14.264.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileOutput, Plus, FileText, Trash2, Pencil, Eye } from "lucide-react";
import { API, authH, fmtCAD, downloadFile, Section, Btn, inputCls, thCls, tdCls, err } from "./_shared";

export const ROE_STATUS = {
  draft: { label: "Brouillon", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  issued: { label: "Émis", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export function RoeBadge({ doc, testId }) {
  const s = ROE_STATUS[doc.status] || ROE_STATUS.draft;
  return <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${s.cls}`} data-testid={testId}>{s.label}{doc.amends_serial ? " · RE modifié" : ""}</span>;
}

export default function RoeTile({ masterId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [empId, setEmpId] = useState("");
  const [busy, setBusy] = useState("");
  const base = `/admin/payroll/${masterId}/roe`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}${base}`, { headers: authH() });
      setData(r.data);
      if (r.data.auto_created) toast.info(`${r.data.auto_created} brouillon(s) de RE créé(s) automatiquement (date de fin d'emploi saisie)`);
    } catch (e) { err(e); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!empId) return;
    setBusy("create");
    try { const r = await axios.post(`${API}${base}`, { employee_id: empId }, { headers: authH() }); navigate(`/admin/paie/${masterId}/releves-emploi/${r.data.id}`); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Supprimer le brouillon de RE de ${d.employee_name} ?`)) return;
    try { await axios.delete(`${API}${base}/${d.id}`, { headers: authH() }); toast.success("Brouillon supprimé"); load(); } catch (e) { err(e); }
  };
  if (!data) return <p className="text-sm text-slate-500 p-6">Chargement…</p>;
  const reasons = data.codes.reasons;

  return (
    <div className="space-y-4" data-testid="paie-roe">
      <Section title="Nouveau relevé d'emploi" icon={FileOutput} testId="paie-roe-new">
        <p className="text-xs text-slate-600">Un brouillon est créé automatiquement dès qu'une <b>date de fin d'emploi</b> est inscrite dans la fiche d'un employé. Vous pouvez aussi en créer un manuellement (congé, maladie, maternité…). Tout est modifiable avant l'émission.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <select className={inputCls} value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="paie-roe-employee-select">
            <option value="">— Choisir un employé —</option>
            {data.employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.employee_no ? ` (${e.employee_no})` : ""}{e.termination_date ? ` · fin ${e.termination_date}` : ""}</option>)}
          </select>
          <Btn tone="sky" icon={Plus} busy={busy === "create"} disabled={!empId} onClick={create} testId="paie-roe-create">Créer le brouillon</Btn>
        </div>
      </Section>

      <Section title={`Relevés d'emploi (${data.items.length})`} icon={FileText} testId="paie-roe-list">
        {data.items.length === 0 ? <p className="text-xs text-slate-500" data-testid="paie-roe-empty">Aucun relevé d'emploi pour ce dossier.</p> : (
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200"><tr>
                <th className={thCls}>Employé</th><th className={thCls}>Statut</th><th className={thCls}>Premier jour · Dernier jour payé</th><th className={thCls}>Raison</th>
                <th className={`${thCls} text-right`}>15A heures</th><th className={`${thCls} text-right`}>15B gains</th><th className={thCls}>N° série</th><th className={`${thCls} text-right`}>Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/60" data-testid={`paie-roe-row-${d.id}`}>
                    <td className={`${tdCls} font-semibold`}>{d.employee_name}<div className="text-[10px] text-slate-400 font-normal">{d.employee_no}{!d.data.b8_sin_ok && <span className="text-rose-600 font-semibold"> · NAS manquant</span>}</div></td>
                    <td className={tdCls}><RoeBadge doc={d} testId={`paie-roe-status-${d.id}`} /></td>
                    <td className={`${tdCls} text-xs font-mono`}>{d.data.b10_first_day || "—"} → {d.data.b11_last_day_paid || "—"}</td>
                    <td className={`${tdCls} text-xs`}>{d.data.b16_reason_code ? `${d.data.b16_reason_code} — ${reasons[d.data.b16_reason_code]}` : <span className="text-amber-700">à choisir</span>}</td>
                    <td className={`${tdCls} text-right font-mono text-xs`}>{Number(d.data.b15a_hours || 0).toFixed(2)}</td>
                    <td className={`${tdCls} text-right font-mono text-xs`}>{fmtCAD(d.data.b15b_earnings)}</td>
                    <td className={`${tdCls} font-mono text-xs`}>{d.serial_no || <span className="text-slate-400">—</span>}</td>
                    <td className={`${tdCls} text-right whitespace-nowrap`}>
                      <Btn tone="ghost" icon={d.status === "draft" ? Pencil : Eye} onClick={() => navigate(`/admin/paie/${masterId}/releves-emploi/${d.id}`)} title={d.status === "draft" ? "Modifier" : "Consulter"} testId={`paie-roe-open-${d.id}`} />
                      <Btn tone="ghost" icon={FileText} busy={busy === `pdf-${d.id}`} onClick={() => downloadFile(`${base}/${d.id}/pdf`, `RE_${d.employee_no || d.employee_name}.pdf`, (b) => setBusy(b ? `pdf-${d.id}` : ""))} title="PDF" testId={`paie-roe-pdf-${d.id}`} />
                      {d.status === "draft" && <Btn tone="ghost" icon={Trash2} onClick={() => remove(d)} title="Supprimer le brouillon" testId={`paie-roe-delete-${d.id}`} className="text-rose-600" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
